import CopyToClipboard from '@bumble/app/components/CopyToClipboard';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Box, Button, Divider, FormControl, LinearProgress, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface GenerateCloudDataProps {
  node: string;
  type: 'service' | 'endpoint';
  onClose?: () => void | undefined;
  secondaryBtn?: () => void | undefined;
}
const GenerateCloudData = ({ node, type = 'endpoint', onClose, secondaryBtn }: GenerateCloudDataProps) => {
  const api = useApi();

  const { data, refetch, isFetching, isSuccess } = useQuery(
    ['prepareEndpointNodeDownload'],
    api.generateNodeCloudInitDataApiV1GenerateNodeCloudInitDataPost.bind(null, {
      node_id: node,
    }),
    { enabled: false, cacheTime: 0, initialData: undefined },
  );
  const handleGenerateData = () => {
    refetch();
  };

  const endpointAddress = useMutation(api.setEndpointNodeFormFactorApiV1SetEndpointNodeFormFactorPost, {
    onSuccess() {
      toast.success('Endpoint Node address received.');
      onClose?.();
    },
    onError: apiError,
  });
  const serviceAddress = useMutation(api.setServiceNodeFormFactorApiV1SetServiceNodeFormFactorPost, {
    onSuccess() {
      toast.success('Service Node address received.');
      onClose?.();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    if (type === 'endpoint') {
      endpointAddress.mutate({
        node_id: node,
        form_factor: 'ami',
      });
    } else {
      serviceAddress.mutate({
        node_id: node,
        form_factor: 'ami',
      });
    }
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 5 }}>
        Click this button to generate the cloud initialization data required for launching an AWS AMI. After generating the data, you will be prompted to copy the output and use it in the AWS console to launch your AMI.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
        <Button
          variant="outlined"
          type="submit"
          disabled={isFetching || isSuccess}
          onClick={handleGenerateData}
        >
          Generate Cloud Init Data
        </Button>
        <CopyToClipboard
          label="Copy Cloud Init Data"
          content={data?.data || ''}
          disabled={!isSuccess}
        />
      </Box>
      {isFetching && <LinearProgress variant="indeterminate" sx={{ width: '100%', mt: 5 }} />}
      <FormControl fullWidth sx={{ mt: 5 }}>
        <TextField
          fullWidth
          label="Cloud Init Data"
          variant="outlined"
          value={isSuccess ? data?.data : ''}
          multiline
          minRows={4}
          maxRows={4}
          disabled
        />
      </FormControl>
      <Divider sx={{ my: 5, width: '100%' }} />
      <Box display="flex" justifyContent={secondaryBtn ? 'space-between' : 'center'}>
        {secondaryBtn && (
          <Button
            variant="outlined"
            onClick={secondaryBtn}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Save Form Factor
        </Button>
      </Box>
    </Box>
  );
};

export default GenerateCloudData;
